<template>
  <div class="db-notifications">
    <!-- Not using b-overlay since it doesn't work well for month view loading -->
    <div v-if="isDbStatsLoading" class="d-flex justify-content-center">
      <b-spinner class="mt-5" label="Spinning"></b-spinner>
    </div>
    <div v-show="!isDbStatsLoading">
      <template v-if="!numDocuments">
        <div class="d-flex justify-content-between">
          <h5 class="text-secondary px-1 my-3">
            There have been no updates the past {{ numDaysText }}.
          </h5>
          <div class="d-flex flex-column">
            <label
              style="align-self: center"
              class="text-nowrap pt-2 mb-0 mt-0 text-secondary"
            >
              Time Range
            </label>
            <b-form-radio-group
              class="align-self-end mt-0 mb-2 mx-2"
              v-model="selectedDate"
              :options="dateOptions"
              button-variant="outline-secondary"
              buttons
              size="sm"
            ></b-form-radio-group>
          </div>
        </div>
      </template>
      <template v-else>
        <div>
          <div class="mh-100" style="width: 1030px">
            <b-form-group>
              <b-form-input
                v-model="documentTitleFilter"
                size="lg"
                placeholder="Search by document title..."
                type="text"
                debounce="100"
              ></b-form-input>
              <hr class="my-2" />
              <div class="d-flex justify-content-between">
                <h6 class="text-secondary p-2 pb-0 mb-0 text-nowrap">
                  Showing {{ filteredDocumentItems.length }} results.
                </h6>
                <b-button
                  variant="link"
                  v-b-toggle.doc-filters
                  class="my-0 mx-1 p-0 text-right p-0 m-0 border-0"
                >
                  <small>{{
                    !showFilters ? 'Show Filters' : 'Hide Filters'
                  }}</small>
                </b-button>
              </div>
              <b-collapse v-model="showFilters" id="doc-filters">
                <MultiSelect
                  @tagChange="documentCategoryChange"
                  :options="ALL_DOCUMENT_CATEGORIES"
                  :value="selectedDocumentCategories"
                  placeholder="Select/Remove document categories..."
                />
              </b-collapse>
            </b-form-group>
          </div>
          <div class="d-flex justify-content-between">
            <b-tabs class="align-self-start">
              <b-tab title="All Agencies" active @click="agency = undefined">
              </b-tab>
              <b-tab
                :key="org"
                v-for="org in ALL_AGENCIES"
                :title="org"
                @click="agency = org"
              >
              </b-tab>
            </b-tabs>
            <div class="d-flex flex-column">
              <label
                style="align-self: center"
                class="text-nowrap pt-2 mb-0 mt-0 text-secondary"
              >
                Time Range
              </label>
              <b-form-radio-group
                class="align-self-end mt-0 mb-2 mx-2"
                v-model="selectedDate"
                :options="dateOptions"
                button-variant="outline-secondary"
                buttons
                size="sm"
              ></b-form-radio-group>
            </div>
          </div>
          <div>
            <DocumentNotificationsList
              :docs="notificationDocs"
              :selectedDocumentCategories="selectedDocumentCategories"
              :documentTitleFilter="documentTitleFilter"
              :fields="fields"
              :filteredDocumentItems="filteredDocumentItems"
              @openDocument="openDocument"
            />
          </div>
        </div>
        <template v-if="showPDFViewerModal">
          <PDFJSViewer
            :isInProject="false"
            :selectedDoc="selectedDoc"
            :selectedDocPageNum="1"
            :showButtons="false"
            @closePdfViewerModal="closeDocument"
          />
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import {
  ALL_AGENCIES,
  ALL_DOCUMENT_CATEGORIES,
  DAY_MS,
  DRUG_PACKAGES,
  GUIDELINES,
  LABEL,
} from '@/constants/constants';
import { getAWSDateTime } from '@/utils/dateUtils';
import DocumentNotificationsList from '@/components/DocumentNotificationsList.vue';
import PDFJSViewer from '@/components/PDFJSViewer.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import { getUniqueProductNames } from '@/operators/searchModifiers';

const ONE_DAYS_AGO_MS = Date.now() - DAY_MS * 1;
const ONE_WEEK_AGO_MS = Date.now() - DAY_MS * 7;
const ONE_MONTH_AGO_MS = Date.now() - DAY_MS * 31;

export default {
  name: 'DocumentNotificationsSection',
  components: {
    DocumentNotificationsList,
    PDFJSViewer,
    MultiSelect,
  },

  data() {
    return {
      showPDFViewerModal: false,
      showFilters: false,
      selectedDoc: null,
      selectedDocumentCategories: ALL_DOCUMENT_CATEGORIES,
      documentTitleFilter: '',
      agency: undefined,
      selectedDate: ONE_DAYS_AGO_MS,
    };
  },
  computed: {
    ...mapGetters(['numDocuments', 'notificationDocs', 'isDbStatsLoading']),

    numDaysText() {
      const numDaysAgo = Math.floor((Date.now() - this.selectedDate) / DAY_MS);
      if (numDaysAgo > 7) {
        return 'month';
      } else if (numDaysAgo > 2) {
        return 'week';
      } else {
        return 'two days';
      }
    },
    filteredDocumentItems() {
      return this.documentItems.filter(({ documentTitle }) =>
        documentTitle
          .toLowerCase()
          .includes(this.documentTitleFilter.toLowerCase())
      );
    },
    documentItems() {
      return this.notificationDocs.reduce((acc, cur) => {
        const agencyName = cur.agency_name;
        const category = cur.doc_category;

        if (this.agency && this.agency !== agencyName) {
          return acc;
        }

        if (!this.selectedDocumentCategories.includes(category)) {
          return acc;
        }

        const curDocs = cur.docs.map((doc) => {
          const productNames = getUniqueProductNames(doc._source.products);
          return {
            agencyName: agencyName || 'N/A',
            category,
            documentTitle:
              doc._source.doc_title || doc._source.doc_type || category,
            date: new Date(doc._source.action_date).toLocaleDateString(),
            guidanceStatus: doc._source.guidance_status
              ? doc._source.guidance_status
              : 'N/A',
            approvalType: doc._source.doc_sub_category
              ? doc._source.doc_sub_category
              : 'N/A',
            productNames: productNames.length ? productNames : ['N/A'],
            doc,
          };
        });

        return [...acc, ...curDocs];
      }, []);
    },
    shouldIncludeApprovalType() {
      return this.selectedDocumentCategories.includes(LABEL, DRUG_PACKAGES);
    },
    shouldIncludeGuidanceStatus() {
      return this.selectedDocumentCategories.includes(GUIDELINES);
    },
    shouldIncludeProductNames() {
      return this.selectedDocumentCategories.includes(LABEL, DRUG_PACKAGES);
    },
    fields() {
      return [
        ...(!this.agency ? [{ key: 'agencyName', sortable: true }] : []),
        { key: 'category', sortable: true },
        { key: 'documentTitle', sortable: true },
        ...(this.shouldIncludeProductNames
          ? [{ key: 'productNames', sortable: true }]
          : []),
        ...(this.shouldIncludeApprovalType
          ? [{ key: 'approvalType', sortable: true }]
          : []),
        ...(this.shouldIncludeGuidanceStatus
          ? [{ key: 'guidanceStatus', sortable: true }]
          : []),
        { key: 'date', sortable: true },
      ];
    },
  },
  watch: {
    selectedDate() {
      this.getDbStatsAsync();
    },
  },
  created() {
    this.DAY_MS = DAY_MS;
    this.ALL_AGENCIES = ALL_AGENCIES;
    this.ALL_DOCUMENT_CATEGORIES = ALL_DOCUMENT_CATEGORIES;
    this.dateOptions = [
      { text: 'Day', value: ONE_DAYS_AGO_MS },
      { text: 'Week', value: ONE_WEEK_AGO_MS },
      { text: 'Month', value: ONE_MONTH_AGO_MS },
    ];
  },
  mounted() {
    this.getDbStatsAsync();
  },
  methods: {
    ...mapActions(['fetchDbStats']),
    getDbStatsAsync() {
      return this.fetchDbStats({
        dateSince: getAWSDateTime(true, this.selectedDate).split('T')[0],
        includeDocs: true,
      });
    },
    openDocument(doc) {
      this.selectedDoc = doc;
      this.showPDFViewerModal = true;
    },
    closeDocument() {
      this.showPDFViewerModal = false;
      this.selectedDoc = null;
    },
    documentCategoryChange(value) {
      this.selectedDocumentCategories = value;
    },
  },
};
</script>

<style scoped>
.db-notifications {
  display: flex;
  flex-direction: column;
}
</style>
